import React, { useState, useEffect } from 'react';
import { Box, Grid, TextField, Typography, Chip, Stack, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { getAllCompetences } from '../../config/shared_config';

// Formulaire pour éditer contextFilters avec vraies données via getAllCompetences()
// Structure attendue:
// {
//   competence: { domain, competence, notion },
//   questionType: string,
//   schoolLevel: string | string[],
//   difficulty: string | string[],
//   priority: number
// }

const levels = ['CP','CE1','CE2','CM1','CM2','6ème'];
const difficulties = ['easy','medium','hard','expert'];

const PromptContextForm = ({ value = {}, onChange }) => {
  const ctx = value || {};
  const comp = ctx.competence || {};

  // Charger toutes les compétences une seule fois
  const allCompetences = React.useMemo(() => getAllCompetences() || [], []);

  // Extraire les domaines uniques
  const domains = React.useMemo(() => {
    const domainsSet = new Set(allCompetences.map(c => c.domain));
    return Array.from(domainsSet).sort();
  }, [allCompetences]);

  // États locaux pour les listes déroulantes
  const [availableCompetences, setAvailableCompetences] = useState([]);
  const [availableNotions, setAvailableNotions] = useState([]);

  // Mettre à jour les compétences disponibles quand le domaine change
  useEffect(() => {
    if (comp.domain) {
      const competencesForDomain = allCompetences
        .filter(c => c.domain === comp.domain)
        .map(c => c.competence);
      const uniqueCompetences = Array.from(new Set(competencesForDomain)).sort();
      setAvailableCompetences(uniqueCompetences);
    } else {
      setAvailableCompetences([]);
    }
  }, [comp.domain, allCompetences]);

  // Mettre à jour les notions disponibles quand la compétence change
  useEffect(() => {
    if (comp.domain && comp.competence) {
      const notionsForCompetence = allCompetences
        .filter(c => c.domain === comp.domain && c.competence === comp.competence)
        .map(c => c.notion);
      const uniqueNotions = Array.from(new Set(notionsForCompetence)).sort();
      setAvailableNotions(uniqueNotions);
    } else {
      setAvailableNotions([]);
    }
  }, [comp.domain, comp.competence, allCompetences]);

  const handleField = (path, val) => {
    const next = { ...ctx };
    if (path.startsWith('competence.')) {
      next.competence = { ...(ctx.competence || {}) };
      const key = path.split('.')[1];
      next.competence[key] = val || undefined;
      
      // Si on change le domaine, réinitialiser compétence et notion
      if (key === 'domain') {
        next.competence.competence = undefined;
        next.competence.notion = undefined;
      }
      // Si on change la compétence, réinitialiser la notion
      if (key === 'competence') {
        next.competence.notion = undefined;
      }
      
      if (!next.competence.domain && !next.competence.competence && !next.competence.notion) {
        delete next.competence;
      }
    } else {
      next[path] = val;
    }
    onChange?.(next);
  };

  const asArray = (v) => Array.isArray(v) ? v : (v ? [v] : []);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Ciblage par compétence</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Domaine</InputLabel>
            <Select
              value={comp.domain || ''}
              label="Domaine"
              onChange={(e) => handleField('competence.domain', e.target.value)}
            >
              <MenuItem value=""><em>-- Aucun --</em></MenuItem>
              {domains.map(d => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth disabled={!comp.domain}>
            <InputLabel>Compétence</InputLabel>
            <Select
              value={comp.competence || ''}
              label="Compétence"
              onChange={(e) => handleField('competence.competence', e.target.value)}
            >
              <MenuItem value=""><em>-- Aucune --</em></MenuItem>
              {availableCompetences.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth disabled={!comp.competence}>
            <InputLabel>Notion</InputLabel>
            <Select
              value={comp.notion || ''}
              label="Notion"
              onChange={(e) => handleField('competence.notion', e.target.value)}
            >
              <MenuItem value=""><em>-- Aucune --</em></MenuItem>
              {availableNotions.map(n => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Qualificateurs</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField label="Type de question" value={ctx.questionType || ''} onChange={(e) => handleField('questionType', e.target.value)} fullWidth />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField select label="Niveaux (multi)" value={asArray(ctx.schoolLevel)} onChange={(e) => handleField('schoolLevel', Array.from(e.target.selectedOptions).map(o => o.value))} fullWidth SelectProps={{ multiple: true, native: true }}>
            {levels.map(l => (<option key={l} value={l}>{l}</option>))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField select label="Difficultés (multi)" value={asArray(ctx.difficulty)} onChange={(e) => handleField('difficulty', Array.from(e.target.selectedOptions).map(o => o.value))} fullWidth SelectProps={{ multiple: true, native: true }}>
            {difficulties.map(d => (<option key={d} value={d}>{d}</option>))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Priorité" value={ctx.priority ?? 0} onChange={(e) => handleField('priority', Number(e.target.value))} fullWidth />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {comp.domain && <Chip label={`Domaine: ${comp.domain}`} />}
        {comp.competence && <Chip label={`Compétence: ${comp.competence}`} />}
        {comp.notion && <Chip label={`Notion: ${comp.notion}`} />}
        {ctx.questionType && <Chip label={`Type: ${ctx.questionType}`} />}
        {asArray(ctx.schoolLevel).length > 0 && <Chip label={`Niveaux: ${asArray(ctx.schoolLevel).join(', ')}`} />}
        {asArray(ctx.difficulty).length > 0 && <Chip label={`Difficulté: ${asArray(ctx.difficulty).join(', ')}`} />}
        {(ctx.priority || ctx.priority === 0) && <Chip label={`Priorité: ${ctx.priority}`} />}
      </Stack>
    </Box>
  );
};

export default PromptContextForm;
